import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts'
import { candidateApi, recruiterApi } from '@/services/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Users,
  FileText,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  Upload,
} from 'lucide-react'

type CandidateRow = {
  uuid: string
  name: string
  extraction_status: string
  created_at: string
}

type FormRow = {
  uuid: string
  job_description: string | null
  created_at: string
}

type SentFormRow = {
  uuid: string
  candidate_name: string | null
  job_description: string | null
  score: number | null
  complete: number
  created_at: string
  updated_at: string
}

type ActivityItem = {
  id: string
  title: string
  detail: string
  at: Date
  href: string
  icon: 'candidate' | 'form' | 'sent' | 'done'
}

const chartConfig = {
  candidates: { label: 'מועמדים חדשים', color: 'hsl(var(--chart-1))' },
  sent: { label: 'שאלונים שנשלחו', color: 'hsl(var(--chart-2))' },
  completed: { label: 'שאלונים שהושלמו', color: 'hsl(var(--chart-4))' },
} satisfies ChartConfig

function parseDate(value: string | null | undefined) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function dayKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function countBetween(dates: Array<Date | null>, start: Date, end: Date) {
  return dates.filter((date) => date && date >= start && date < end).length
}

function windowNote(current: number, previous: number) {
  if (current === 0 && previous === 0) return 'אין חדשים ב-30 הימים האחרונים'
  if (current === previous) return `${current} ב-30 הימים האחרונים, כמו בחלון הקודם`
  const diff = Math.abs(current - previous)
  const direction = current > previous ? 'יותר' : 'פחות'
  return `${current} ב-30 הימים האחרונים, ${diff} ${direction} מ-30 הימים שלפניהם`
}

function relativeTime(date: Date) {
  const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000))
  if (minutes < 1) return 'לפני רגע'
  if (minutes < 60) return `לפני ${minutes} דק׳`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `לפני ${hours} שע׳`
  const days = Math.floor(hours / 24)
  if (days < 30) return `לפני ${days} ימים`
  return date.toLocaleDateString('he-IL')
}

function shortText(value: string | null | undefined, max = 72) {
  const text = value?.trim().split('\n')[0]?.trim()
  if (!text) return 'ללא תיאור משרה'
  return text.length > max ? `${text.slice(0, max)}…` : text
}

export default function DashboardPage() {
  const [candidates, setCandidates] = useState<CandidateRow[]>([])
  const [forms, setForms] = useState<FormRow[]>([])
  const [sentForms, setSentForms] = useState<SentFormRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setIsLoading(true)
      setError(null)
      try {
        const [candidateRes, formRes, sentRes] = await Promise.all([
          candidateApi.getCandidates(),
          recruiterApi.getForms(),
          recruiterApi.getSentForms(),
        ])
        if (cancelled) return
        if (
          candidateRes.status !== 'success' ||
          formRes.status !== 'success' ||
          sentRes.status !== 'success'
        ) {
          setError('שגיאה בטעינת לוח הבקרה')
          return
        }
        setCandidates(candidateRes.candidates)
        setForms(formRes.forms)
        setSentForms(sentRes.sentforms)
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'שגיאה בטעינת לוח הבקרה')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const snapshot = useMemo(() => {
    const now = new Date()
    const start30 = new Date(now)
    start30.setDate(start30.getDate() - 30)
    const start60 = new Date(now)
    start60.setDate(start60.getDate() - 60)

    const candidateDates = candidates.map((row) => parseDate(row.created_at))
    const formDates = forms.map((row) => parseDate(row.created_at))
    const completedRows = sentForms.filter((row) => row.complete === 1)
    const pendingCount = sentForms.length - completedRows.length
    const scores = completedRows
      .map((row) => row.score)
      .filter((score): score is number => typeof score === 'number')
    const averageScore = scores.length
      ? scores.reduce((sum, score) => sum + score, 0) / scores.length
      : null
    const completionRate = sentForms.length
      ? Math.round((completedRows.length / sentForms.length) * 100)
      : null

    const extraction = {
      partial: candidates.filter((row) => row.extraction_status === 'partial').length,
      failed: candidates.filter((row) => row.extraction_status === 'failed').length,
    }

    const days = Array.from({ length: 30 }, (_, index) => {
      const date = new Date(now)
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() - (29 - index))
      return {
        date: dayKey(date),
        candidates: 0,
        sent: 0,
        completed: 0,
      }
    })
    const byDay = new Map(days.map((day) => [day.date, day]))
    const bump = (value: string | null | undefined, key: 'candidates' | 'sent' | 'completed') => {
      const date = parseDate(value)
      if (!date) return
      const bucket = byDay.get(dayKey(date))
      if (bucket) bucket[key] += 1
    }
    candidates.forEach((row) => bump(row.created_at, 'candidates'))
    sentForms.forEach((row) => bump(row.created_at, 'sent'))
    completedRows.forEach((row) => bump(row.updated_at, 'completed'))

    const activity: ActivityItem[] = [
      ...candidates.flatMap((row) => {
        const at = parseDate(row.created_at)
        if (!at) return []
        return [{
          id: `candidate-${row.uuid}`,
          title: 'מועמד נוסף',
          detail: row.name || 'ללא שם',
          at,
          href: '/candidates',
          icon: 'candidate' as const,
        }]
      }),
      ...forms.flatMap((row) => {
        const at = parseDate(row.created_at)
        if (!at) return []
        return [{
          id: `form-${row.uuid}`,
          title: 'שאלון נוצר',
          detail: shortText(row.job_description),
          at,
          href: `/forms/${row.uuid}/overview`,
          icon: 'form' as const,
        }]
      }),
      ...sentForms.flatMap((row) => {
        const sentAt = parseDate(row.created_at)
        const items: ActivityItem[] = []
        if (sentAt) {
          items.push({
            id: `sent-${row.uuid}`,
            title: 'שאלון נשלח',
            detail: row.candidate_name || 'מועמד',
            at: sentAt,
            href: `/sentform/${row.uuid}/candidate-details`,
            icon: 'sent',
          })
        }
        if (row.complete === 1) {
          const doneAt = parseDate(row.updated_at)
          if (doneAt) {
            const scoreText = typeof row.score === 'number' ? ` · ציון ${row.score.toFixed(1)}/10` : ''
            items.push({
              id: `done-${row.uuid}`,
              title: 'שאלון הושלם',
              detail: `${row.candidate_name || 'מועמד'}${scoreText}`,
              at: doneAt,
              href: `/sentform/${row.uuid}/candidate-details`,
              icon: 'done',
            })
          }
        }
        return items
      }),
    ]
      .sort((a, b) => b.at.getTime() - a.at.getTime())
      .slice(0, 6)

    const formStats = new Map<string, { title: string; sent: number; completed: number }>()
    sentForms.forEach((row) => {
      const key = row.job_description?.trim() || ''
      const current = formStats.get(key) ?? { title: shortText(row.job_description, 42), sent: 0, completed: 0 }
      current.sent += 1
      if (row.complete === 1) current.completed += 1
      formStats.set(key, current)
    })
    const topForms = [...formStats.entries()]
      .map(([key, value]) => ({ key: key || 'empty', ...value }))
      .sort((a, b) => b.sent - a.sent)
      .slice(0, 4)

    return {
      candidatesRecent: countBetween(candidateDates, start30, now),
      candidatesPrevious: countBetween(candidateDates, start60, start30),
      formsRecent: countBetween(formDates, start30, now),
      formsPrevious: countBetween(formDates, start60, start30),
      pendingCount,
      completedCount: completedRows.length,
      averageScore,
      completionRate,
      extraction,
      chart: days,
      activity,
      topForms,
    }
  }, [candidates, forms, sentForms])

  const sentProgress = sentForms.length
    ? Math.round((snapshot.completedCount / sentForms.length) * 100)
    : 0

  return (
    <div className="bg-background p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div data-tour="tour-welcome">
          <h1 className="text-3xl font-bold tracking-tight">לוח בקרה</h1>
          <p className="text-muted-foreground mt-1">
            מצב המועמדים, השאלונים והמענה כרגע
          </p>
        </div>
        {isLoading && <LoadingSpinner size="sm" />}
      </div>

      {error && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 pt-6">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => setReloadKey((value) => value + 1)}>
              נסה שוב
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-tour="tour-stats">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סך המועמדים</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? '…' : candidates.length.toLocaleString('he-IL')}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {isLoading ? 'טוען נתונים' : windowNote(snapshot.candidatesRecent, snapshot.candidatesPrevious)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">שאלונים</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? '…' : forms.length.toLocaleString('he-IL')}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {isLoading ? 'טוען נתונים' : windowNote(snapshot.formsRecent, snapshot.formsPrevious)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ממתינים למענה</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? '…' : snapshot.pendingCount.toLocaleString('he-IL')}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {isLoading
                ? 'טוען נתונים'
                : `מתוך ${sentForms.length.toLocaleString('he-IL')} שאלונים שנשלחו`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">הושלמו</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? '…' : snapshot.completedCount.toLocaleString('he-IL')}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {isLoading
                ? 'טוען נתונים'
                : snapshot.completionRate === null
                  ? 'עדיין לא נשלחו שאלונים'
                  : `שיעור השלמה ${snapshot.completionRate}%${
                      snapshot.averageScore === null ? '' : ` · ציון ממוצע ${snapshot.averageScore.toFixed(1)}/10`
                    }`}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>פעילות ב-30 הימים האחרונים</CardTitle>
            <CardDescription>
              מועמדים חדשים, שאלונים שנשלחו ושאלונים שהושלמו
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="aspect-auto h-[300px] w-full">
              <AreaChart data={snapshot.chart}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value) =>
                    new Date(value).toLocaleDateString('he-IL', { month: 'short', day: 'numeric' })
                  }
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString('he-IL', {
                          day: 'numeric',
                          month: 'long',
                        })
                      }
                      indicator="dot"
                    />
                  }
                />
                <Area
                  dataKey="candidates"
                  type="monotone"
                  fill="var(--color-candidates)"
                  fillOpacity={0.2}
                  stroke="var(--color-candidates)"
                />
                <Area
                  dataKey="sent"
                  type="monotone"
                  fill="var(--color-sent)"
                  fillOpacity={0.2}
                  stroke="var(--color-sent)"
                />
                <Area
                  dataKey="completed"
                  type="monotone"
                  fill="var(--color-completed)"
                  fillOpacity={0.2}
                  stroke="var(--color-completed)"
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>פעילות אחרונה</CardTitle>
            <CardDescription>האירועים האחרונים במערכת</CardDescription>
          </CardHeader>
          <CardContent>
            {snapshot.activity.length === 0 ? (
              <p className="text-sm text-muted-foreground">עדיין אין פעילות להצגה</p>
            ) : (
              <div className="space-y-4">
                {snapshot.activity.map((item) => (
                  <Link key={item.id} to={item.href} className="flex items-start gap-3 rounded-md hover:bg-accent/60 p-1 -m-1">
                    <div className="rounded-full bg-primary/10 p-2">
                      {item.icon === 'candidate' && <Users className="h-4 w-4 text-primary" />}
                      {item.icon === 'form' && <FileText className="h-4 w-4 text-primary" />}
                      {item.icon === 'sent' && <Send className="h-4 w-4 text-primary" />}
                      {item.icon === 'done' && <CheckCircle2 className="h-4 w-4 text-primary" />}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.detail}</p>
                      <p className="text-xs text-muted-foreground">{relativeTime(item.at)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>מצב המענה</CardTitle>
            <CardDescription>
              {sentForms.length === 0
                ? 'לא נשלחו שאלונים'
                : `${snapshot.completedCount} הושלמו מתוך ${sentForms.length}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${sentProgress}%` }} />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">ממתינים</span>
              <span className="font-medium">{snapshot.pendingCount}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">הושלמו</span>
              <span className="font-medium">{snapshot.completedCount}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">חילוץ קורות חיים שנכשל</span>
              <span className="font-medium">{snapshot.extraction.failed}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">חילוץ חלקי</span>
              <span className="font-medium">{snapshot.extraction.partial}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>שאלונים לפי היקף שליחה</CardTitle>
          </CardHeader>
          <CardContent>
            {snapshot.topForms.length === 0 ? (
              <p className="text-sm text-muted-foreground">עדיין לא נשלחו שאלונים</p>
            ) : (
              <div className="space-y-3">
                {snapshot.topForms.map((form) => (
                  <div key={form.key} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{form.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {form.sent} נשלחו · {form.completed} הושלמו
                      </p>
                    </div>
                    <Badge variant={form.completed === form.sent ? 'secondary' : 'default'}>
                      {form.completed === form.sent ? 'הושלם' : 'פתוח'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>פעולות</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button className="w-full" asChild>
              <Link to="/recruiter">
                <Plus />
                יצירת שאלון
              </Link>
            </Button>
            <Button className="w-full" variant="outline" asChild>
              <Link to="/upload">
                <Upload />
                העלאת קורות חיים
              </Link>
            </Button>
            <Button className="w-full" variant="outline" asChild>
              <Link to="/sentforms">
                <Send />
                שאלונים שנשלחו
              </Link>
            </Button>
            {snapshot.extraction.failed > 0 && (
              <p className="flex items-start gap-2 pt-2 text-xs text-muted-foreground">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {snapshot.extraction.failed} קורות חיים לא חולצו. אפשר לנסות שוב מרשימת המועמדים.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
